import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../api/client';
import trendingFireIcon from '../assets/images/trending-fire-icon.png';
import './TrendingTagsBar.css';

const STALE_MS = 5 * 60 * 1000;
const HEAT_TIER_COUNT = 5;
const SCROLL_EDGE_THRESHOLD = 4;

/** Preview heat palettes: ?trendingPalette=a (gold ramp) | b (blue depth) */
function getTrendingPalette() {
  const param = new URLSearchParams(window.location.search).get('trendingPalette');
  return param === 'b' ? 'b' : 'a';
}

async function fetchTrendingTags() {
  const res = await api.get('/api/tags/trending');
  return res.data;
}

function pinVariant(item) {
  const slug = String(item?.slug || '').toLowerCase();
  if (slug.includes('featured')) return 'featured';
  if (slug.includes('latest')) return 'latest';
  return 'featured';
}

function TrendingFireIcon() {
  return (
    <span className="trending-tags-bar__trend-icon" role="img" aria-label="Trending">
      <img
        src={trendingFireIcon}
        alt=""
        className="trending-tags-bar__trend-icon-img"
        width={22}
        height={32}
        decoding="async"
        draggable={false}
      />
    </span>
  );
}

function PinLinks({ pins }) {
  if (!pins.length) return null;
  return (
    <div className="trending-tags-bar__pins">
      {pins.map((item) => (
        <Link
          key={item.slug || item.href}
          to={item.href}
          className={`trending-tags-bar__pin trending-tags-bar__pin--${pinVariant(item)}`}
        >
          {String(item.display || item.label || '').replace(/^#+/, '')}
        </Link>
      ))}
    </div>
  );
}

/**
 * Assign heat tiers by score group (not per-tag rank).
 * Tags with the same trending_score always share a tier; groups are spread
 * evenly from hottest (tier 4) to coldest (tier 0) for visible contrast.
 * Sorted desc by trending_score from GET /api/tags/trending.
 */
function heatTiersForTags(tags) {
  if (!tags.length) return [];

  const groups = [];
  for (const tag of tags) {
    const score = Number(tag.trending_score) || 0;
    const last = groups[groups.length - 1];
    if (last && last.score === score) {
      last.tags.push(tag);
    } else {
      groups.push({ score, tags: [tag] });
    }
  }

  const groupCount = groups.length;
  return groups.flatMap((group, groupIndex) => {
    const tier =
      groupCount <= 1
        ? HEAT_TIER_COUNT - 1
        : Math.min(
            HEAT_TIER_COUNT - 1,
            Math.round((1 - groupIndex / (groupCount - 1)) * (HEAT_TIER_COUNT - 1))
          );
    return group.tags.map((tag) => ({ ...tag, heatTier: tier }));
  });
}

export default function TrendingTagsBar() {
  const scrollRef = useRef(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const palette = useMemo(() => getTrendingPalette(), []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tags', 'trending'],
    queryFn: fetchTrendingTags,
    staleTime: STALE_MS,
    gcTime: 12 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const pins = data?.pins || [];
  const tags = useMemo(() => data?.tags ?? [], [data?.tags]);
  const hasItems = pins.length > 0 || tags.length > 0;
  const shouldRender = !isError && (isLoading || hasItems);

  const tagsWithHeat = useMemo(() => heatTiersForTags(tags), [tags]);
  const showTrendingMarker = tagsWithHeat.length > 0 || (isLoading && !hasItems);

  const scrollStep = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return 160;
    return Math.max(160, Math.floor(el.clientWidth * 0.65));
  }, []);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const canBack = el.scrollLeft > SCROLL_EDGE_THRESHOLD;
    setCanScrollBack((prev) => (prev === canBack ? prev : canBack));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    let raf = 0;
    const scheduleUpdate = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        updateScrollButtons();
      });
    };

    scheduleUpdate();
    el.addEventListener('scroll', scheduleUpdate, { passive: true });
    const ro = new ResizeObserver(scheduleUpdate);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', scheduleUpdate);
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [updateScrollButtons, tagsWithHeat.length, isLoading, pins.length]);

  const scrollForward = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const step = scrollStep();
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - SCROLL_EDGE_THRESHOLD;
    if (!atEnd) {
      el.scrollBy({ left: step, behavior: 'smooth' });
    }
  }, [scrollStep]);

  const scrollBackward = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const step = scrollStep();
    el.scrollBy({ left: -step, behavior: 'smooth' });
  }, [scrollStep]);

  if (!shouldRender) {
    return null;
  }

  return (
    <section
      className="trending-tags-strip"
      aria-label="Trending topics"
      data-palette={palette}
    >
      <div className="trending-tags-strip__inner">
        {showTrendingMarker ? (
          <div className="trending-tags-bar__anchor trending-tags-bar__anchor--desktop">
            <div className="trending-tags-bar__anchor-inner">
              <PinLinks pins={pins} />
              <span className="trending-tags-bar__label">Trending</span>
            </div>
          </div>
        ) : null}
        <div className="trending-tags-bar__scroll" ref={scrollRef}>
          {showTrendingMarker ? (
            <div className="trending-tags-bar__scroll-lead trending-tags-bar__scroll-lead--mobile">
              <PinLinks pins={pins} />
              <TrendingFireIcon />
            </div>
          ) : null}
          {isLoading && !hasItems ? (
            <span className="trending-tags-bar__skeleton" aria-hidden />
          ) : null}
          {tagsWithHeat.map((item) => (
            <Link
              key={item.slug || item.href}
              to={item.href}
              className={`trending-tags-bar__tag trending-tags-bar__tag--heat-${item.heatTier}`}
            >
              {item.display || `#${item.label || item.slug}`}
            </Link>
          ))}
        </div>
        <div className="trending-tags-bar__scroll-controls">
          {canScrollBack ? (
            <button
              type="button"
              className="trending-tags-bar__scroll-btn trending-tags-bar__scroll-btn--back"
              onClick={scrollBackward}
              aria-label="Scroll trending topics back"
            >
              <span aria-hidden>&lt;</span>
            </button>
          ) : null}
          <button
            type="button"
            className="trending-tags-bar__scroll-btn trending-tags-bar__scroll-btn--forward"
            onClick={scrollForward}
            aria-label="Scroll trending topics forward"
          >
            <span aria-hidden>&gt;</span>
          </button>
        </div>
      </div>
    </section>
  );
}
